import { Box, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { ResetRecordModal } from "./components/ResetRecordModal";
import { useLocation, useParams } from "react-router-dom";
import {
  ColumnDef,
  DataTable,
  SortOrder,
} from "../../components/DataTable/DataTable";
import SelectedItemsPreview from "../../components/SelectedItemsPreview/SelectedItemsPreview";
import { usePageMetadata } from "../../context/PageMetadataContext";
import {
  GetTrainingRecordResult,
  UserAssignmentAllTrainingRecords,
} from "../../services/api/types/editTraniningRecords/editTrainingRecords";
import { ProgressStatus } from "../../services/api/types/general/homeAssignment";
import { GetUserResult, Roles } from "../../services/api/types/users/users";
import { brand, gray } from "../../theme/themePrimitives";
import {
  FILTERED_SELECTED_USERS_KEY,
  LEGACY_SELECTED_USERS_KEY,
  MANUAL_SELECTED_USERS_KEY,
  SELECTED_USERS_SOURCE_KEY,
} from "./EditTrainingRecords/EditTrainingRecords";
import TrainingRecordForm, {
  TrainingRecordFormValues,
} from "./components/TrainingRecordForm";
import { useAuth } from "../../context/AuthContext";
import {
  GetUserAssignmentAllTrainingRecordsPaginatedResult,
  editTrainingRecord,
  getSingleTrainingRecordInfo,
  resetTrainingRecord,
} from "../../services/api/apiClient";
import { useToast } from "../../context/ToastContext";
import dayjs from "dayjs";

function getSelectedUsersFromStorage(): GetUserResult[] {
  try {
    const parseUsers = (key: string): GetUserResult[] => {
      const stored = localStorage.getItem(key);
      if (!stored) return [];
      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? parsed : [];
    };

    const source = localStorage.getItem(SELECTED_USERS_SOURCE_KEY) as
      | "manual"
      | "filtered"
      | null;

    const manualUsers = parseUsers(MANUAL_SELECTED_USERS_KEY);
    const filteredUsers = parseUsers(FILTERED_SELECTED_USERS_KEY);
    const legacyUsers = parseUsers(LEGACY_SELECTED_USERS_KEY);

    if (source === "filtered") {
      return filteredUsers.length ? filteredUsers : legacyUsers;
    }
    if (source === "manual") {
      return manualUsers;
    }
    if (filteredUsers.length) return filteredUsers;
    if (manualUsers.length) return manualUsers;
    return legacyUsers;
  } catch {
    return [];
  }
}

export const TrainingRecords = () => {
  const location = useLocation();
  const { portalUid } = useParams();

  const externalSelectedUsers: GetUserResult[] = useMemo(() => {
    const fromState = location.state?.selectedUsers as
      | GetUserResult[]
      | undefined;
    if (fromState && fromState.length > 0) return fromState;
    return getSelectedUsersFromStorage();
  }, [location.state]);

  const trainingRecord: GetTrainingRecordResult | null =
    (location.state?.trainingRecord as GetTrainingRecordResult) ?? null;

  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [resetRecordUid, setResetRecordUid] = useState<string | null>(null);

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [sort, setSort] = useState<{ orderBy: string; order: SortOrder }>({
    orderBy: "record",
    order: "asc",
  });

  const { auth } = useAuth();
  const { showToast } = useToast();

  const [attempts, setAttempts] = useState<UserAssignmentAllTrainingRecords[]>(
    [],
  );
  const [attemptsTotal, setAttemptsTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedAttempt, setSelectedAttempt] =
    useState<UserAssignmentAllTrainingRecords | null>(null);

  const [selectedRecordInfo, setSelectedRecordInfo] = useState<any>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleRowClick = async (row: UserAssignmentAllTrainingRecords) => {
    if (!portalUid) return;
    setSelectedAttempt(row);
    const record = await getSingleTrainingRecordInfo(portalUid, {
      recordId: row.UserAssignmentUid,
    });
    setSelectedRecordInfo(record);
  };

  useEffect(() => {
    const fetchAttempts = async () => {
      if (!portalUid || !trainingRecord?.UserAssignmentUid) return;
      setLoading(true);
      try {
        const data = await GetUserAssignmentAllTrainingRecordsPaginatedResult(
          portalUid,
          {
            UserPortalUid: externalSelectedUsers[0]?.UserPortalUid ?? "",
            PortalAssignmentUid: trainingRecord.PortalAssignmentUid ?? "",
            limit: rowsPerPage,
            offset: (page - 1) * rowsPerPage,
            orderBy: sort.orderBy,
            sort: sort.order,
          },
        );
        const rows = data?.rows ?? [];
        setAttempts(rows);
        setAttemptsTotal(data?.count ?? 0);
      } finally {
        setLoading(false);
      }
    };
    fetchAttempts();
  }, [
    externalSelectedUsers,
    page,
    portalUid,
    rowsPerPage,
    sort.order,
    sort.orderBy,
    trainingRecord.PortalAssignmentUid,
    trainingRecord?.UserAssignmentUid,
    refreshKey,
  ]);
  const handleFormSubmit = async (values: TrainingRecordFormValues) => {
    if (!portalUid || !selectedAttempt) return;
    setIsSubmitting(true);
    try {
      await editTrainingRecord(portalUid, selectedAttempt.UserAssignmentUid, {
        progressStatus: values.status as ProgressStatus,
        scores: values.score || undefined,
        startDate: values.startDate
          ? values.startDate.format("YYYY-MM-DD")
          : undefined,
        completionDate: values.completionDate
          ? values.completionDate.format("YYYY-MM-DD")
          : undefined,
        maxAttempts: values.maxAttempts,
      });
      showToast("Training record updated successfully", "success");
      setRefreshKey((k) => k + 1);
      const updatedRecord = await getSingleTrainingRecordInfo(portalUid, {
        recordId: selectedAttempt.UserAssignmentUid,
      });
      setSelectedRecordInfo(updatedRecord);
    } catch {
      showToast("Failed to update training record", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetConfirm = async () => {
    if (!portalUid || !resetRecordUid) return;
    await resetTrainingRecord(portalUid, resetRecordUid);
    setRefreshKey((k) => k + 1);
    setSelectedRecordInfo(null);
    setResetModalOpen(false);
    setResetRecordUid(null);
  };

  const isHiddenTrainingRecords =
    auth.user?.roles?.includes(Roles.PORTAL_ADMIN) ||
    auth.user?.roles?.includes(Roles.ADMIN);

  const isViewOnly =
    auth.user?.roles.includes(Roles.MAIN_ADMIN) &&
    !auth.user?.roles.includes(Roles.SYSTEM_ADMIN);

  const isMainAndSystemAdmin =
    auth.user?.roles?.includes(Roles.MAIN_ADMIN) ||
    auth.user?.roles?.includes(Roles.SYSTEM_ADMIN);

  const formInitialValues = useMemo(() => {
    if (!selectedRecordInfo) return undefined;
    return {
      status: selectedRecordInfo.progressStatus ?? "",
      score: selectedRecordInfo.scores ?? "",
      startDate: selectedRecordInfo.startDate
        ? dayjs(selectedRecordInfo.startDate)
        : null,
      completionDate: selectedRecordInfo.completionDate
        ? dayjs(selectedRecordInfo.completionDate)
        : null,
      maxAttempts: (() => {
        const raw = selectedRecordInfo.attempts?.split("/")[1];
        const n = Number(raw);
        return raw && !isNaN(n) ? n : undefined;
      })(),
    };
  }, [selectedRecordInfo]);

  const columns: ColumnDef<UserAssignmentAllTrainingRecords>[] = [
    {
      id: "record",
      label: "Record",
      sortable: true,
      getValue: (row) => String(row.recordNo),
    },
    {
      id: "lastAccessed",
      label: "Last Accessed",
      sortable: true,
      getValue: (row) => dayjs(row.UpdatedAt).format("DD-MMM-YYYY HH:mm"),
    },
    {
      id: "startDate",
      label: "Start Date",
      sortable: true,
      getValue: (row) =>
        row.startDate ? dayjs(row.startDate).format("DD-MMM-YYYY HH:mm") : "",
    },
    {
      id: "usageTime",
      label: "Usage Time",
      sortable: true,
      getValue: (row) => row.timeSpent,
    },
    {
      id: "score",
      label: "Score",
      sortable: true,
      getValue: (row) => row.scores,
    },
    {
      id: "noAttempts",
      label: "No. attempts",
      sortable: false,
      render: (row) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <span>{row.attempts}</span>
          {row.reset && isMainAndSystemAdmin && (
            <Typography
              onClick={() => {
                setResetRecordUid(row.UserAssignmentUid);
                setResetModalOpen(true);
              }}
              sx={{
                color: brand[600],
                fontWeight: 500,
                "&:hover": { textDecoration: "underline", cursor: "pointer" },
              }}
            >
              Reset
            </Typography>
          )}
        </Box>
      ),
      getValue: (row) => row.attempts,
    },
  ];

  usePageMetadata(
    {
      title: "Training record",
      breadcrumbs: [
        {
          label: "User management",
          path: `/portal-configuration`,
        },
        {
          label: "User search",
          path: `/portal-configuration/${portalUid}/users`,
        },
        {
          label: "Edit training records",
          path: `/portal-configuration/${portalUid}/users/edit-training-records`,
        },
        { label: "Training record" },
      ],
    },
    [portalUid],
  );

  return (
    <>
      <SelectedItemsPreview
        items={externalSelectedUsers.map((user) => ({
          id: user.UserPortalUid,
          title: `${user.firstName} ${user.lastName}`,
          subtitle: user.email,
        }))}
        itemType="users"
        previewTitle={
          externalSelectedUsers[0]
            ? `${externalSelectedUsers[0].firstName} ${externalSelectedUsers[0].lastName}`
            : ""
        }
        previewDescription={externalSelectedUsers[0]?.email ?? ""}
        showViewDetailsButton={false}
      />

      {trainingRecord && (
        <SelectedItemsPreview
          items={[
            {
              id: trainingRecord.UserAssignmentUid,
              title: trainingRecord.title,
              subtitle: trainingRecord.type,
            },
          ]}
          itemType="assignments"
          previewTitle={trainingRecord.title}
          previewDescription={trainingRecord.type}
          showViewDetailsButton={false}
        />
      )}

      <DataTable
        data={attempts}
        columns={columns}
        totalCount={attemptsTotal}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptionsOverride={[5]}
        onPageChange={setPage}
        onRowsPerPageChange={(rows) => {
          setRowsPerPage(rows);
          setPage(1);
        }}
        sort={sort}
        onSortChange={(orderBy, order) => {
          setSort({ orderBy, order });
          setPage(1);
        }}
        getRowId={(row) => row.UserAssignmentUid}
        isLoading={loading}
        onRowClick={isMainAndSystemAdmin ? handleRowClick : undefined}
        getRowSx={
          isMainAndSystemAdmin
            ? (row) =>
                selectedAttempt?.UserAssignmentUid === row.UserAssignmentUid
                  ? {
                      backgroundColor: `${gray[200]} !important`,
                      "&:hover": { backgroundColor: `${gray[200]} !important` },
                    }
                  : {}
            : undefined
        }
      />

      {!isHiddenTrainingRecords && (
        <>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2, mt: 4 }}>
            Update training records
          </Typography>
          <TrainingRecordForm
            isViewOnly={isViewOnly}
            initialValues={formInitialValues}
            isSubmitting={isSubmitting}
            onSubmit={handleFormSubmit}
          />
        </>
      )}
      <ResetRecordModal
        open={resetModalOpen}
        onClose={() => {
          setResetModalOpen(false);
          setResetRecordUid(null);
        }}
        onConfirm={handleResetConfirm}
      />
    </>
  );
};
